import tokens from '@kluein/design-tokens';
import { Check, Warning, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { DigestPickerModal } from 'components/common/digest-picker-modal';
import { NotifierType, useNotifier } from 'hooks/use-notifier';
import { useAddAnswerToDigestMutation } from 'store/api/digests';

import type { DigestPickerSubmitResult } from 'components/common/digest-picker-modal';
import type { ReactNode } from 'react';

export type AddAnswerToDigestModalProps = {
  // Opaque id assigned by the Ask Klue service (in modern chat this is the
  // assistant message id). Klue uses it only as a provenance / idempotence
  // key on the resulting Post.
  answerId: string;
  // Answer content converted to HTML by the parent (markdown -> HTML +
  // citation source expansion). Backend infers split-vs-single from any
  // <section data-article-url> markers embedded here.
  answerHtml: string;
  // Optional fallbacks for the item title when the answer is materialized
  // in single-mode. Backend order: title -> userQuestion -> "Ask Klue Answer".
  title?: string;
  userQuestion?: string;
  onClose: () => void;
};

// Thin Ask-Klue-answer adapter over the shared DigestPickerModal. Owns the
// answer-scoped mutation and toast copy; presentation comes from the shared
// component (same modal shell as the card-side Add-to-Digest).
const AddAnswerToDigestModal = ({
  answerId,
  answerHtml,
  title,
  userQuestion,
  onClose,
}: AddAnswerToDigestModalProps): ReactNode => {
  const { t } = useTranslation(['Dashboard', 'Card', 'Common']);
  const { notify } = useNotifier();
  const [addAnswerToDigest] = useAddAnswerToDigestMutation();

  const handleSubmit = async (
    digestIds: number[],
  ): Promise<DigestPickerSubmitResult> => {
    const results = await Promise.allSettled(
      digestIds.map((emailDigestId) =>
        addAnswerToDigest({
          answerId,
          answerHtml,
          emailDigestId,
          title,
          userQuestion,
        }).unwrap(),
      ),
    );

    let totalAdded = 0;
    let totalAlreadyAdded = 0;
    let anyFailed = false;
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        totalAdded += r.value.added ?? 0;
        totalAlreadyAdded += r.value.alreadyAdded ?? 0;
      } else {
        anyFailed = true;
      }
    });

    return { totalAdded, totalAlreadyAdded, anyFailed };
  };

  const handleSuccess = ({
    totalAdded,
    totalAlreadyAdded,
    anyFailed,
  }: DigestPickerSubmitResult): void => {
    if (anyFailed) {
      notify({
        type: NotifierType.ERROR,
        message: t('Dashboard:askKlue.addToDigest.error'),
        icon: <X size={tokens.iconSize.small} />,
      });
      return;
    }
    if (totalAdded === 0 && totalAlreadyAdded > 0) {
      notify({
        type: NotifierType.INFO,
        message: t('Dashboard:askKlue.addToDigest.successAlreadyAdded'),
        icon: <Warning size={tokens.iconSize.small} />,
      });
    } else if (totalAdded > 1) {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Dashboard:askKlue.addToDigest.successSplit', {
          count: totalAdded,
        }),
        icon: <Check size={tokens.iconSize.small} />,
      });
    } else {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Dashboard:askKlue.addToDigest.successSingle'),
        icon: <Check size={tokens.iconSize.small} />,
      });
    }
    onClose();
  };

  return (
    <DigestPickerModal
      title={t('Dashboard:askKlue.addToDigest.title')}
      contentHtml={answerHtml}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onClose={onClose}
    />
  );
};

export default AddAnswerToDigestModal;
